<script>
    import { onMount } from "svelte";
    import { authState } from "@app/store/authStore.svelte.js";
    import { showModal } from "@app/store/modalStore.svelte.js";
    import { uiState } from "@app/store/uiStore.svelte.js";
    import {
        knpState,
        loadKnpGuides,
        selectKnpGuide,
        saveKnpTerm,
        deleteKnpTerm,
    } from "@app/store/knpStore.svelte.js";
    import { toast } from "@zerodevx/svelte-toast";
    import { v4 as uuidv4 } from "uuid";
    import { Grid, Willow } from "@svar-ui/svelte-grid";
    import { diff } from "deep-object-diff";
    import { slide } from "svelte/transition";
    import { linear } from "svelte/easing";
    import Swal from "sweetalert2";
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import * as m from "@app/paraglide/messages.js";

    let searchQuery = $state("");
    let selectedGuideId = $state(null);
    let api = $state();
    let originalGridData = $state([]);
    let history = $derived(api?.getReactiveState()?.history);
    let offcanvasWidth = $state(40); // Width in vw
    let isResizing = $state(false);

    // Derived state for admin/manager check
    let isAdminOrManager = $derived(
        authState.user &&
            (authState.user.role === "admin" ||
                authState.user.role === "manager"),
    );

    // Datagrid columns
    let gridColumns = $derived.by(() => {
        if (!knpState.selectedGuide) return [];

        const columns = [
            {
                id: "term",
                header: m.knp_lookup_term_column(),
                width: 200,
                editor: "text",
            },
            {
                id: "type",
                header: m.knp_lookup_type_column(),
                width: 150,
                editor: "richselect",
                options: [
                    { id: "character", label: "Character" },
                    { id: "phrase", label: "Phrase" },
                    { id: "location", label: "Location" },
                    { id: "organization", label: "Organization" },
                    { id: "title", label: "Title" }
                ],
            },
        ];

        // Add columns for each language
        knpState.selectedGuide.languages?.forEach((lang) => {
            columns.push({
                id: lang,
                header: lang.toUpperCase(),
                width: 200,
                editor: "text",
            });
        });

        return columns;
    });

    // Datagrid rows
    let gridRows = $derived.by(() => {
        let filteredTerms = knpState.terms;

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filteredTerms = filteredTerms.filter((term) => {
                if (term.term.toLowerCase().includes(query)) return true;
                if (term.type.toLowerCase().includes(query)) return true;

                if (term.translations) {
                    for (let lang in term.translations) {
                        if (
                            term.translations[lang]
                                ?.toLowerCase()
                                .includes(query)
                        ) {
                            return true;
                        }
                    }
                }

                return false;
            });
        }

        return filteredTerms.map((term) => ({
            id: term.id,
            term: term.term,
            type: term.type,
            ...(term.translations || {}),
        }));
    });

    // Update originalGridData when terms change
    $effect(() => {
        if (!searchQuery.trim() && knpState.terms.length > 0) {
            const transformedData = knpState.terms.map((term) => ({
                id: term.id,
                term: term.term,
                type: term.type,
                ...(term.translations || {}),
            }));
            originalGridData = JSON.parse(JSON.stringify(transformedData));
        }
    });

    // Load guides when component mounts
    onMount(() => {
        loadKnpGuides();
    });

    // Load terms when guide is selected
    $effect(() => {
        if (selectedGuideId) {
            // Only select if it's different from current
            if (selectedGuideId !== knpState.selectedGuide?.id) {
                const guide = knpState.guides.find(
                    (g) => g.id === selectedGuideId,
                );
                if (guide) {
                    selectKnpGuide(guide);
                }
            }
        } else {
            // Deselect if null is selected
            if (knpState.selectedGuide !== null) {
                selectKnpGuide(null);
            }
        }
    });

    function addNewTerm() {
        if (!knpState.selectedGuide) return;

        const newTerm = {
            id: uuidv4(),
            term: m.knp_lookup_new_term_default(),
            type: "phrase",
            translations: {},
        };

        knpState.selectedGuide.languages?.forEach((lang) => {
            newTerm.translations[lang] = "";
        });

        saveKnpTerm(newTerm)
            .then(() => {
                toast.push(m.knp_lookup_new_term_added_toast(), {
                    classes: ["toast-success"],
                });
            })
            .catch((error) => {
                console.error("Error adding term:", error);
                toast.push(
                    m.knp_lookup_add_term_failed_toast({
                        message: error.message,
                    }),
                    {
                        classes: ["toast-danger"],
                    },
                );
            });
    }

    async function handleSaveChanges() {
        if (!api) {
            toast.push(m.knp_lookup_grid_not_initialized_toast(), {
                classes: ["toast-danger"],
            });
            return;
        }

        let gridState = api.getState();
        let { data: currentData } = gridState;

        const originalMap = new Map(
            originalGridData.map((row) => [row.id, row]),
        );
        const currentMap = new Map(currentData.map((row) => [row.id, row]));

        const toUpdate = [];
        const toDelete = [];
        const toCreate = [];

        for (const currentRow of currentData) {
            const originalRow = originalMap.get(currentRow.id);

            if (!originalRow) {
                toCreate.push(currentRow);
            } else {
                const differences = diff(originalRow, currentRow);

                if (Object.keys(differences).length > 0) {
                    toUpdate.push(currentRow);
                }
            }
        }

        for (const originalRow of originalGridData) {
            if (!currentMap.has(originalRow.id)) {
                toDelete.push(originalRow.id);
            }
        }

        if (
            toUpdate.length === 0 &&
            toDelete.length === 0 &&
            toCreate.length === 0
        ) {
            toast.push(m.knp_lookup_no_changes_to_save_toast(), {
                classes: ["toast-info"],
            });
            return;
        }

        const savePromises = [];

        for (const row of toUpdate) {
            const term = knpState.terms.find((t) => t.id === row.id);
            if (!term) continue;

            term.term = row.term;
            term.type = row.type;

            if (!term.translations) term.translations = {};
            knpState.selectedGuide.languages?.forEach((lang) => {
                if (row[lang] !== undefined) {
                    term.translations[lang] = row[lang];
                }
            });

            savePromises.push(saveKnpTerm(term));
        }

        for (const row of toCreate) {
            const newTerm = {
                id: row.id,
                term: row.term,
                type: row.type,
                translations: {},
            };

            knpState.selectedGuide.languages?.forEach((lang) => {
                if (row[lang] !== undefined) {
                    newTerm.translations[lang] = row[lang];
                }
            });

            savePromises.push(saveKnpTerm(newTerm));
        }

        try {
            await Promise.all(savePromises);
            originalGridData = JSON.parse(JSON.stringify(currentData));

            const totalChanges =
                toUpdate.length + toDelete.length + toCreate.length;
            toast.push(
                m.knp_lookup_saved_changes_toast({ count: totalChanges }),
                {
                    classes: ["toast-success"],
                },
            );
        } catch (error) {
            console.error("Error saving changes:", error);
            toast.push(
                m.knp_lookup_save_changes_failed_toast({
                    message: error.message,
                }),
                {
                    classes: ["toast-danger"],
                },
            );
        }
    }

    function handleUndo() {
        api?.exec("undo");
    }

    function handleRedo() {
        api?.exec("redo");
    }

    async function handleDeleteSelected() {
        let selectedRows = api.getState().selectedRows;

        if (!selectedRows || selectedRows.length === 0) {
            toast.push(m.knp_lookup_no_terms_selected_toast(), {
                classes: ["toast-warning"],
            });
            return;
        }

        const result = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.destructive,
            title: m.knp_lookup_delete_terms_title_confirm(),
            text: m.knp_lookup_delete_terms_text({
                count: selectedRows.length,
            }),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: m.knp_lookup_delete_confirm(),
            cancelButtonText: m.knp_lookup_delete_cancel(),
        });

        if (result.isConfirmed) {
            const deletePromises = selectedRows.map((id) => deleteKnpTerm(id));

            try {
                await Promise.all(deletePromises);
                toast.push(
                    m.knp_lookup_deleted_terms_toast({
                        count: selectedRows.length,
                    }),
                    {
                        classes: ["toast-success"],
                    },
                );
            } catch (error) {
                console.error("Error deleting terms:", error);
                toast.push(
                    m.knp_lookup_delete_failed_toast({
                        message: error.message,
                    }),
                    {
                        classes: ["toast-danger"],
                    },
                );
            }
        }
    }

    function openKnpManager() {
        showModal("knpManager");
    }

    function startResize(e) {
        isResizing = true;
        e.preventDefault();
    }

    function resize(e) {
        if (!isResizing) return;

        // Calculate width based on mouse position from right edge
        const viewportWidth = window.innerWidth;
        const distanceFromRight = viewportWidth - e.clientX;
        const newWidth = (distanceFromRight / viewportWidth) * 100;

        // Clamp between 10vw and 90vw
        offcanvasWidth = Math.max(25, Math.min(100, newWidth));
    }

    function stopResize() {
        isResizing = false;
    }

    // Add event listeners for resize
    $effect(() => {
        if (isResizing) {
            window.addEventListener("mousemove", resize);
            window.addEventListener("mouseup", stopResize);

            return () => {
                window.removeEventListener("mousemove", resize);
                window.removeEventListener("mouseup", stopResize);
            };
        }
    });
</script>

<div
    transition:slide={{ duration: 250, axis: "x", easing: linear }}
    class="offcanvas offcanvas-end {uiState.knpLookup ? 'show' : ''}"
    tabindex="-1"
    id="knpOffcanvas"
    aria-labelledby="knpOffcanvasLabel"
    style="width: {offcanvasWidth}vw; {uiState.knpLookup
        ? 'visibility: visible;'
        : ''}"
>
    <!-- Resize Handle -->
    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
    <div
        class="resize-handle"
        onmousedown={startResize}
        role="separator"
        aria-orientation="vertical"
    ></div>

    <div class="offcanvas-header">
        <h5 class="offcanvas-title" id="knpOffcanvasLabel">
            <i class="bi bi-journal-text me-2"></i>{m.knp_terminology_lookup()}
        </h5>
        <button
            type="button"
            class="btn-close"
            onclick={() => (uiState.knpLookup = false)}
            aria-label={m.close()}
        ></button>
    </div>
    <div class="offcanvas-body">
        {#if !authState.team?.id}
            <div class="alert alert-warning" role="alert">
                <i class="bi bi-exclamation-triangle-fill me-2"></i>
                {m.knp_lookup_requires_team_account()}
            </div>
        {:else}
            <!-- Guide Selector -->
            <div class="mb-3">
                <label for="knpGuideSelect" class="form-label"
                    >{m.knp_lookup_select_guide_label()}</label
                >
                <select
                    class="form-select"
                    id="knpGuideSelect"
                    bind:value={selectedGuideId}
                >
                    <option value={null}
                        >{m.knp_lookup_select_guide_placeholder()}</option
                    >
                    {#each knpState.guides as guide (guide.id)}
                        <option value={guide.id}>
                            {guide.title}
                        </option>
                    {/each}
                </select>
                {#if knpState.guides.length === 0}
                    <small class="form-text text-muted">
                        {m.knp_lookup_no_guides_available()}
                        <button
                            type="button"
                            class="btn btn-link btn-sm p-0"
                            onclick={openKnpManager}
                        >
                            {m.knp_lookup_create_one()}
                        </button>
                    </small>
                {:else if isAdminOrManager}
                    <small class="form-text text-muted">
                        <i class="bi bi-info-circle me-1"></i>
                        {m.knp_lookup_showing_guides_info({
                            count: knpState.guides.length,
                        })}
                    </small>
                {:else if knpState.guides.some((g) => g.groups && g.groups.length > 0)}
                    <small class="form-text text-muted">
                        <i class="bi bi-info-circle me-1"></i>
                        {m.knp_lookup_guides_restricted_info()}
                    </small>
                {/if}
            </div>

            {#if knpState.selectedGuide}
                <!-- Toolbar -->
                <div
                    class="d-flex justify-content-between align-items-center mb-3"
                >
                    <div class="d-flex gap-2 align-items-center">
                        <div class="input-group" style="max-width: 250px;">
                            <span class="input-group-text">
                                <i class="bi bi-search"></i>
                            </span>
                            <input
                                type="text"
                                class="form-control form-control-sm"
                                placeholder={m.knp_lookup_search_placeholder()}
                                bind:value={searchQuery}
                            />
                        </div>

                        <!-- Add/Delete Group -->
                        <div class="btn-group btn-group-sm">
                            <button
                                class="btn btn-success"
                                onclick={addNewTerm}
                                title={m.knp_lookup_add_term_title()}
                            >
                                <i class="bi bi-plus-lg"></i>
                            </button>
                            <button
                                class="btn btn-danger"
                                onclick={handleDeleteSelected}
                                title={m.knp_lookup_delete_terms_title()}
                            >
                                <i class="bi bi-trash"></i>
                            </button>
                        </div>

                        <!-- Undo/Redo Group -->
                        <div class="btn-group btn-group-sm">
                            <button
                                class="btn btn-outline-secondary"
                                onclick={handleUndo}
                                disabled={history && !$history?.undo}
                                title={m.knp_lookup_undo_title()}
                            >
                                <i class="bi bi-arrow-counterclockwise"></i>
                            </button>
                            <button
                                class="btn btn-outline-secondary"
                                onclick={handleRedo}
                                disabled={history && !$history?.redo}
                                title={m.knp_lookup_redo_title()}
                            >
                                <i class="bi bi-arrow-clockwise"></i>
                            </button>
                        </div>
                    </div>

                    <!-- Save Changes Button -->
                    <button
                        class="btn btn-primary btn-sm"
                        onclick={() => handleSaveChanges()}
                        title={m.knp_lookup_save_changes_title()}
                    >
                        <i class="bi bi-floppy me-1"></i>
                        {m.knp_lookup_save_button()}
                    </button>
                </div>

                <!-- Terms Datagrid -->
                {#if knpState.loading}
                    <div class="text-center py-5">
                        <div
                            class="spinner-border spinner-border-sm"
                            role="status"
                        >
                            <span class="visually-hidden">{m.loading()}</span>
                        </div>
                    </div>
                {:else if knpState.terms.length === 0}
                    <div class="alert alert-info" role="alert">
                        {m.knp_lookup_no_terms_yet()}
                    </div>
                {:else}
                    <div class="knp-datagrid-container" style="height: 65svh;">
                        <Willow>
                            <Grid
                                data={gridRows}
                                columns={gridColumns}
                                bind:this={api}
                                selection={true}
                                undo={true}
                            />
                        </Willow>
                    </div>
                    <p class="small text-muted mt-2 mb-0">
                        <i class="bi bi-info-circle"></i>
                        {m.knp_lookup_edit_help()}
                    </p>
                {/if}

                <div class="mt-3 text-center">
                    <button
                        type="button"
                        class="btn btn-link btn-sm"
                        onclick={openKnpManager}
                    >
                        <i class="bi bi-gear"></i>
                        {m.knp_lookup_manage_guides_button()}
                    </button>
                </div>
            {:else}
                <div class="alert alert-info" role="alert">
                    {m.knp_lookup_select_guide_prompt()}
                </div>
            {/if}
        {/if}
    </div>
</div>

<style>
    :global(.knp-datagrid-container) {
        border: 1px solid #dee2e6;
        border-radius: 0.375rem;
        overflow: hidden;
    }

    .resize-handle {
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 8px;
        cursor: ew-resize;
        background: transparent;
        z-index: 1050;
        transition: background-color 0.2s;
    }

    .resize-handle:hover {
        background-color: rgba(0, 123, 255, 0.2);
    }

    .resize-handle:active {
        background-color: rgba(0, 123, 255, 0.4);
    }
</style>
